import ReactGA from 'react-ga'
import { LOCATION_CHANGE } from 'react-router-redux'
import { matchPath } from 'react-router'
import {
  TOGGLE_SIDEBAR,
  HIDE_SIDEBAR,
  SET_SIDEBAR_SECTION,
  RESET_SIDEBAR_SECTION,
  SET_SIDEBAR_FILTER,
  RESET_SIDEBAR_FILTER,
  SET_EDITING_ITEM,
  SET_SIDEBAR_DRAG
} from '../actionTypes'
import {
  accessListSelector,
  sidebarDragSelector
} from '../selectors/accessSelectors'
import {
  sidebarShowingSelector,
  sidebarSectionSelector,
  sidebarFoldersSelector,
  sidebarItemsSelector
} from '../selectors/sidebarSelectors'
import { EDITOR, BOARDS, VIEWED, NAME, FILTERS } from '../../constants'
import { routes } from '../../../variables'
import { rowHeight, sidebarWidth } from '../../style'
import { moveItem } from './tree'

export const toggleSidebar = show => (dispatch, getState) => {
  show = show === undefined ? !sidebarShowingSelector(getState()) : show
  dispatch({ type: TOGGLE_SIDEBAR, payload: { show } })
}

export const hideSidebar = () => ({ type: HIDE_SIDEBAR })

export const setSidebarSection = section => {
  return {
    type: SET_SIDEBAR_SECTION,
    payload: { section }
  }
}
export const resetSidebarSection = () => {
  return {
    type: RESET_SIDEBAR_SECTION
  }
}
export const setSidebarFilter = filter => (dispatch, getState) =>
  dispatch({
    type: SET_SIDEBAR_FILTER,
    payload: { filter, section: sidebarSectionSelector(getState()) }
  })
export const resetSidebarFilter = () => (dispatch, getState) =>
  dispatch({
    type: RESET_SIDEBAR_FILTER,
    payload: { section: sidebarSectionSelector(getState()) }
  })

export const setEditingItem = item => {
  return {
    type: SET_EDITING_ITEM,
    payload: { item }
  }
}

let dragItemId = null
export const getDragItemId = () => dragItemId
let pastThreshold = false
let startPosition
export const dragStart = (event, data) => (dispatch, getState) => {
  dragItemId = null
  pastThreshold = false // not until we move far enough (4px)
  startPosition = data

  const target = event.target.closest('[data-id]')
  const itemId = target.dataset.id
  const accessList = accessListSelector(getState())

  // check if we can move this item

  // items only have order within their folder - not unfiled items
  const item = accessList[itemId]
  if (!item.parent) return

  // we need permissions on the folder to move it in/out/around
  if (!(accessList[item.parent].accessLevel >= EDITOR)) return

  dragItemId = itemId
}

export const clearDrag = { type: SET_SIDEBAR_DRAG, payload: {} }

export const drag = (event, data) => (dispatch, getState) => {
  if (!dragItemId) return

  // have to move 4 px before it's a real drag
  const threshold = 4
  if (
    !pastThreshold &&
    Math.abs(startPosition.x - data.x) < threshold &&
    Math.abs(startPosition.y - data.y) < threshold
  ) {
    return
  }
  pastThreshold = true

  // dragged too far right
  if (data.x > sidebarWidth) return dispatch(clearDrag)

  // y relative to list top
  let y = event.pageY - data.node.offsetTop
  // convert y from px to rows
  y /= rowHeight.default

  // above top
  if (y < 0) return dispatch(clearDrag)

  const state = getState()
  const folders = sidebarFoldersSelector(state) || []
  const setDrag = drag =>
    dispatch({
      type: SET_SIDEBAR_DRAG,
      payload: { id: dragItemId, x: data.x, y: data.y, ...drag }
    })

  // within folder breadcrumbs section
  if (y < folders.length) {
    const folder = folders[Math.floor(y)]
    return setDrag({ folder: folder.id })
  }

  // within items section
  // need to take offset of this section and scroll into account
  y -= folders.length
  y += data.node.childNodes[1].scrollTop / rowHeight.default
  const items = sidebarItemsSelector(state)
  if (y < items.length) {
    const item = items[Math.floor(y)] // item we are currently over
    const offset = y % 1 // position within row 0=top 1=bottom
    // if draging over another folder and near the middle of it
    if (item.type === 'folder' && offset > 0.3 && offset < 0.8) {
      if (
        dragItemId === item.id || // can't drop in self
        !(item.accessLevel >= EDITOR) // no permission to add to this
      ) {
        return setDrag({ folder: item.id, notAllowed: true })
      }
      // put in that folder
      return setDrag({ folder: item.id })
    }
    // put at position closest to y
    return setDrag({ order: Math.round(y) })
  }

  // below items section by no more than 2 rows
  if (y < items.length + 2) {
    // move to end of items list
    return setDrag({ order: items.length })
  }

  // Too far below last row
  return dispatch(clearDrag)
}

export const dragEnd = (event, data) => (dispatch, getState) => {
  // dragThrottled.cancel()

  const state = getState()
  const drag = sidebarDragSelector(state)
  dispatch(clearDrag)

  // not dragging anywhere
  if (!drag || !drag.id) return

  // // don't bother doing anything if inserting before/after self
  // if ((dragging.insertBefore || dragging.insertAfter) === dragging.id) {
  //   dragging = null
  //   return
  // }

  return dispatch(moveItem(drag))
}

// export const dragThrottled = throttle(drag, 50)

const initialState = {
  open: undefined,
  section: localStorage.getItem('sidebarSection') || BOARDS,
  boardsFilter: localStorage.getItem('sidebarFilter-boards') || VIEWED,
  foldersFilter: localStorage.getItem('sidebarFilter-boards') || NAME,
  drag: {},
  editingItem: null
}
export const sidebarReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case TOGGLE_SIDEBAR:
      return {
        ...state,
        open: payload.show === undefined ? !state.open : payload.show
      }
    case HIDE_SIDEBAR:
      return { ...state, open: false }
    case LOCATION_CHANGE:
      // open sidebar when navigating to a folder or /
      if (payload.pathname === '/') return { ...state, open: true }
      const match = matchPath(payload.pathname, { path: routes.folder })
      if (match) return { ...state, open: true }

      return state
    case SET_SIDEBAR_SECTION:
      // remember section
      localStorage.setItem('sidebarSection', payload.section)
      ReactGA.event(
        {
          category: 'Sidebar',
          action: 'Set Section',
          label: payload.section
        },
        ['app']
      )
      return { ...state, section: payload.section }
    case RESET_SIDEBAR_SECTION:
      ReactGA.event(
        {
          category: 'Sidebar',
          action: 'Set Section',
          label: initialState.section
        },
        ['app']
      )
      return { ...state, section: initialState.section }
    case SET_SIDEBAR_FILTER:
      // remember if it not a true filter
      if (!FILTERS.includes(payload.filter))
        localStorage.setItem('sidebarFilter-' + payload.section, payload.filter)
      ReactGA.event(
        {
          category: 'Sidebar',
          action: `Set ${payload.section} filter`,
          label: payload.filter
        },
        ['app']
      )
      return {
        ...state,
        [payload.section + 'Filter']: payload.filter
      }
    case RESET_SIDEBAR_FILTER:
      const prev = localStorage.getItem('sidebarFilter-' + payload.section)
      ReactGA.event(
        {
          category: 'Sidebar',
          action: `Reset ${payload.section} filter`,
          label: prev
        },
        ['app']
      )
      return {
        ...state,
        [payload.section + 'Filter']: prev
      }

    case SET_EDITING_ITEM:
      return { ...state, editingItem: payload.item, open: true }
    case SET_SIDEBAR_DRAG:
      return { ...state, drag: payload }
    default:
      return state
  }
}
